'use client'

import { Button } from '@brew/ui/button'
import { TabsContent } from '@brew/ui/tabs'
import { SignInButton } from '@clerk/nextjs'
import dynamic from 'next/dynamic'
import { useTranslations } from 'next-intl'
import { useMemo, useState } from 'react'
import type {
  IntegrationCapabilityMeta,
  IntegrationMeta,
} from '@/lib/integrations/catalog'
import { FramerFormGuide } from '../components/framer-form-guide'
import { buildTriggerableCategoriesForIntegration } from '../components/inbound-events-data'
import { InboundEventsSection } from '../components/inbound-events-section'
import { IntegrationDetailFrame } from './integration-detail-frame'
import '../integrations.css'
import {
  BodyGrid,
  Hero,
  MetaRail,
  SignInPlaceholder,
  TabBar,
  useIntegrationTabs,
} from './integration-page-shell'
import { StatusPill } from './integration-sidebar'
import { ProseSection } from './prose-section'

/** Stripe's scopes render behind the Security tab, below the fold. */
const StripeScopesBlock = dynamic(
  () =>
    import('../components/stripe-scopes-block').then((m) => ({
      default: m.StripeScopesBlock,
    })),
  { ssr: false, loading: () => null }
)

interface PublicIntegrationPageProps {
  /** The integration with its public words in the reader's language. */
  integration: IntegrationMeta
  /** What it can do, in the reader's language (`getPublicCapabilities`). */
  capabilities: ReadonlyArray<IntegrationCapabilityMeta>
  /**
   * When the public browse route renders `<PageHero>` above this page,
   * skip the duplicate inline hero.
   */
  marketingLayout?: boolean
}

/**
 * The public mirror of an integration page
 * (`/browse/integrations/[provider]`): the same frame, meta rail and tabs as
 * the signed-in page (`IntegrationPage`), with a Clerk `<SignInButton>` as
 * the call to action and the Setup tab asking the visitor to sign in. A
 * module of its own so the public page never loads the signed-in surface
 * (the connect flows, the Slack, Stripe and Framer panels) or the catalogs
 * only those translate with; it translates with `integrations` (and
 * `common`, from the marketing root).
 */
export function PublicIntegrationPage({
  integration,
  capabilities,
  marketingLayout = false,
}: PublicIntegrationPageProps) {
  return (
    <IntegrationDetailFrame surface="public">
      <PublicSurface
        integration={integration}
        capabilities={capabilities}
        marketingLayout={marketingLayout}
      />
    </IntegrationDetailFrame>
  )
}

function PublicSurface({
  integration,
  capabilities,
  marketingLayout = false,
}: PublicIntegrationPageProps) {
  const t = useTranslations('integrations')
  const tCommon = useTranslations('common')
  const [tab, setTab] = useState('overview')

  const triggerableCategories = useMemo(
    () => buildTriggerableCategoriesForIntegration(integration.id),
    [integration.id]
  )
  const hasTriggerableEvents = triggerableCategories.some(
    (category) => category.events.length > 0
  )

  const status = (
    <StatusPill
      kind={
        integration.availability === 'coming_soon' ? 'coming_soon' : 'available'
      }
    />
  )

  const cta =
    integration.availability === 'coming_soon' ? (
      <Button disabled variant="secondary">
        {tCommon('states.comingSoon')}
      </Button>
    ) : (
      <SignInButton mode="modal">
        <Button>
          {integration.id === 'framer' ? t('cta.addForm') : t('cta.getStarted')}
        </Button>
      </SignInButton>
    )

  const tabs = useIntegrationTabs(
    integration.id === 'stripe'
      ? ['overview', 'setup', 'security']
      : ['overview', 'setup']
  )

  return (
    <>
      {marketingLayout ? null : (
        <Hero integration={integration} cta={cta} status={status} />
      )}
      <BodyGrid
        rail={
          <MetaRail integration={integration} capabilities={capabilities} />
        }
        content={
          <TabBar value={tab} onValueChange={setTab} tabs={tabs}>
            <TabsContent value="overview" className="mt-0 flex flex-col gap-8">
              <ProseSection title={t('sections.overview')}>
                <p>{integration.description}</p>
              </ProseSection>
              {hasTriggerableEvents ? (
                <InboundEventsSection
                  providerName={integration.name}
                  categories={triggerableCategories}
                />
              ) : null}
            </TabsContent>
            <TabsContent value="setup" className="mt-0 flex flex-col gap-6">
              {integration.id === 'framer' && (
                <FramerFormGuide steps={integration.tutorialSteps} />
              )}
              <SignInPlaceholder
                title={t('signIn.title', { name: integration.name })}
                body={t('signIn.body')}
              />
            </TabsContent>
            {integration.id === 'stripe' ? (
              <TabsContent
                value="security"
                className="mt-0 flex flex-col gap-6"
              >
                <ProseSection title={t('stripe.scopes.title')}>
                  <p className="mb-3">{t('sections.stripeScopesPublicBody')}</p>
                  <StripeScopesBlock />
                </ProseSection>
              </TabsContent>
            ) : null}
          </TabBar>
        }
      />
    </>
  )
}
